import {
  useIsMobile,
  WSButton,
  WSElement,
  WSFlexBox,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import * as React from "react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { WS_LINKS } from "../../types/wsLinks";
import { useRefDimensions } from "../../utils/useRefDimensions";
import { AccountIsPausedModal } from "../AccountIsPausedModal/AccountIsPausedModal";
import { BackToAdminAccountBanner } from "../BackToAdminAccountBanner/BackToAdminAccountBanner";
import { BackToRequestingUserAccountBanner } from "../BackToRequestingUserAccountBanner/BackToRequestingUserAccountBanner";
import { MembershipUpgradeModal } from "../Membership/MembershipUpgradeModal";
import { SideNav } from "../SideNav/SideNav.component";
import { SubscriptionBanner } from "../SubscriptionBanner/SubscriptionBanner.component";
import styles from "./styles.module.scss";
import { SideNavHeader } from "../SideNav/SideNavHeader";

const CURRENT_YEAR = new Date().getFullYear();

export const BaseAppLayout: React.FC = ({ children }) => {
  const location = useLocation();
  const [isDrawerOpened, setIsDrawerOpened] = React.useState(false);
  const [isSideNavOpened, setIsSideNavOpened] = React.useState(false);
  const isMobile = useIsMobile();

  const headerRef = useRef<HTMLElement | null>(null);
  const headerDimensions = useRefDimensions(headerRef);

  const { pathname } = useLocation();

  useEffect(() => {
    if (isDrawerOpened) {
      document.body.classList.add(styles.bodyOverflowHidden);
    } else {
      document.body.classList.remove(styles.bodyOverflowHidden);
    }
  }, [isDrawerOpened]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      searchParams.get("showModal") === "referrals" ||
      searchParams.get("bigHappyFamily") === "hellYeah"
    ) {
      setIsDrawerOpened(true);
    }
  }, [location]);

  const copyRightsEl = (
    <WSText.ParagraphSm color="gray500">
      © {CURRENT_YEAR} Wingspan Networks, Inc. All right reserved.
    </WSText.ParagraphSm>
  );

  const privacyPolicyEl = (
    <WSButton.Link
      ml="M"
      onClick={() => window.open(WS_LINKS.privacyPolicy)}
      textClassName={styles.linkBtnText}
    >
      Privacy Policy
    </WSButton.Link>
  );

  const termsOfServiceEl = (
    <WSButton.Link
      ml="M"
      onClick={() => window.open(WS_LINKS.termsOfService)}
      textClassName={styles.linkBtnText}
    >
      Terms of Service
    </WSButton.Link>
  );

  const bankDisclosureEl = (
    <WSText.ParagraphSm mt="XS" color="gray500">
      Wingspan is a financial services technology company and is not a bank.
      Banking services provided by Lead Bank, N.A., Member FDIC.
    </WSText.ParagraphSm>
  );

  return (
    <>
      <AccountIsPausedModal />
      <MembershipUpgradeModal />

      <WSElement
        className={styles.container}
        key="container"
        data-navigationisopen={isMobile ? isDrawerOpened : isSideNavOpened}
        data-testid="appContainer"
      >
        <WSElement ref={headerRef} className={styles.header} key="header">
          <WSScreen.Mobile>
            <SideNavHeader
              isDrawerOpened={isDrawerOpened}
              setIsDrawerOpened={setIsDrawerOpened}
            />
          </WSScreen.Mobile>

          <BackToAdminAccountBanner />
          <BackToRequestingUserAccountBanner />
          <SubscriptionBanner />
        </WSElement>

        <SideNav
          isDrawerOpened={isDrawerOpened}
          setIsDrawerOpened={setIsDrawerOpened}
          isSideNavOpened={isSideNavOpened}
          setIsSideNavOpened={setIsSideNavOpened}
          desktopTopPadding={headerDimensions.height}
        />

        <WSElement
          as="main"
          className={styles.main}
          key="main"
          style={{
            paddingTop: headerDimensions.height
          }}
        >
          <WSElement className={styles.content} key="content">
            {children}
          </WSElement>
          <WSElement as="footer" className={styles.footer}>
            <WSScreen.TabletAndDesktop>
              <WSElement>
                <WSFlexBox.Center>
                  {copyRightsEl}
                  {privacyPolicyEl}
                  {termsOfServiceEl}
                </WSFlexBox.Center>
                {bankDisclosureEl}
              </WSElement>
            </WSScreen.TabletAndDesktop>
            <WSScreen.Mobile>
              <WSFlexBox.Center px="XL" mt="3XL" direction="column">
                {copyRightsEl}
                <WSFlexBox.Center>
                  {privacyPolicyEl}
                  {termsOfServiceEl}
                </WSFlexBox.Center>
                {bankDisclosureEl}
              </WSFlexBox.Center>
            </WSScreen.Mobile>
          </WSElement>
        </WSElement>
      </WSElement>
    </>
  );
};
